// React Hooks
import { useRef, useState, useCallback, useEffect } from "react";

//Librerie esterne
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import he from "he";
import Slider from "react-slick";

//Configurazioni
import { appConfig } from "../../config/appConfig";

//Hooks
import useArticoli from '../../hooks/useGetArticoli';

const LastArticles: React.FC = () => {

    const { t } = useTranslation();

    const formatDate = (dateString: string | null, t: (key: string) => string): string => {
        if (!dateString) return t('blog.date.unavailable');
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return t('blog.date.invalid');
            }
            return date.toLocaleDateString('it-IT', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
            });
        } catch (e) {
            console.error("Error formatting date:", dateString, e);
            return t('blog.date.invalid');
        }
    };

    const { articles } = useArticoli({
        agencyId: appConfig.agency.id,
        initialLimit: 5,
    });

    // Problema scorrimento + click nel carosello
    const [swiping, setSwiping] = useState(false);
    const sliderRef = useRef<Slider | null>(null);

    const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (swiping) e.preventDefault(); // blocca il click se stiamo swipando
    };


    // Animazione per blocchi servizi --------------------------------------------------------------------
    const allRefs = useRef<(HTMLDivElement | null)[]>([]);
    const [visible, setVisible] = useState<boolean[]>([false]);

    // Posizione elementi ref
    const setRef = useCallback((el: HTMLDivElement | null) => {
        if (el && !allRefs.current.includes(el)) {
            allRefs.current.push(el);
        }
    }, []);

    useEffect(() => {
        window.addEventListener('scroll', () => {
            allRefs.current.forEach((ref, index) => {
                if (ref) {
                    const rect = ref.getBoundingClientRect();
                    if (rect.top < window.innerHeight - 250) {
                        setVisible((prev) => {
                            const newVisible = [...prev];
                            newVisible[index] = true;
                            return newVisible;
                        });
                    }
                }
            });
        });
        return () => {
            window.removeEventListener('scroll', () => { });
        };
    }, []);

    // Calcola dinamicamente slidesToShow in base alla larghezza dello schermo
    const [slidesToShow, setSlidesToShow] = useState(() => {
        if (typeof window !== 'undefined') {
            if (window.innerWidth < 576) return 1;
            if (window.innerWidth < 992) return 2;
            return 3;
        }
        return 3;
    });

    const [arrows, setArrows] = useState(() => {
        if (typeof window !== 'undefined') {
            if (window.innerWidth < 576) return false;
            return true;
        }
        return true;
    });

    // Aggiorna slidesToShow al resize
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 576) {
                setSlidesToShow(1);
                setArrows(false);
            } else if (window.innerWidth < 992) {
                setSlidesToShow(2);
                setArrows(true);
            } else {
                setSlidesToShow(3);
                setArrows(true);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const settings = {
        dots: true,
        arrows: arrows,
        infinite: true,
        speed: 1000,
        autoplay: false,
        autoplaySpeed: 3000,
        slidesToShow: slidesToShow,
        slidesToScroll: 1,
        swipeToSlide: true,
        onInit: () => setSwiping(false),
        beforeChange: () => setSwiping(true),
        afterChange: () => setSwiping(false),
    };

    return (
        <section id="last-articles-box" className="space-ptb">
            <div className="container-fluid">
                <div className="section-title text-center">
                    <h2>{t('homePage.lastArticlesSection.title')}</h2>
                    <p>{t('homePage.lastArticlesSection.para')}</p>
                </div>
                <div className={`fade-in ${visible[0] ? 'visible' : ''}`} ref={setRef}>
                    <Slider ref={sliderRef} {...settings}>
                        {articles.map((article, index) => (
                            <div className="px-1 px-md-2 mb-4" key={index}>
                                <Link to={`/blog/${article.id}`} className="mb-3 blog-card" onClick={handleLinkClick}>
                                    <div className="img-wrapper">
                                        <img
                                            className="img-fluid"
                                            src={article.foto ? `https://www.casagest24.it/${article.foto}` : '/images/blog/placeholder.jpg'}
                                            alt={`${article.titolo || t('blog.article.imageAltFallback')}`}
                                        />
                                    </div>
                                    <div className="p-3 text-center">
                                        {article.nomeCategoria && (
                                            <div className="card-lable mb-2">
                                                <strong className="text-gold">{article.nomeCategoria}</strong>
                                            </div>
                                        )}
                                        <h5 dangerouslySetInnerHTML={{ __html: he.decode(article.titolo as string) }} />
                                        <p dangerouslySetInnerHTML={{ __html: he.decode(article.testoRidotto as string).substring(0, 100)+" ..." }} />
                                        <hr className="my-3" />
                                        <div className="public-data">
                                            <i className="far fa-clock mr-1"></i>
                                            <span>{formatDate(article.dataPubblicazione, t)}</span>
                                        </div>

                                    </div>
                                </Link>
                            </div>
                        ))}
                    </Slider>
                </div>
            </div>
        </section>
    )
}
export default LastArticles